// Parsing helpers for the Bloomi planning inputs (forecast, Shopify inventory, Amazon FBA inventory).
// Pure functions — no Supabase calls — so they can be tested in isolation.

// RFC-4180-ish CSV parser: handles quoted fields with commas, escaped quotes and newlines.
// Also handles tab-delimited files (Amazon sometimes serves .txt reports).
export function parseCSVRows(text) {
  const src = text.replace(/^﻿/, '')
  const firstLine = src.slice(0, src.indexOf('\n') === -1 ? src.length : src.indexOf('\n'))
  const delim = (firstLine.match(/\t/g) || []).length > (firstLine.match(/,/g) || []).length ? '\t' : ','
  const rows = []
  let row = [], field = '', inQuotes = false
  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++ } else inQuotes = false
      } else field += c
    } else if (c === '"') inQuotes = true
    else if (c === delim) { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      row.push(field); rows.push(row); row = []; field = ''
    } else field += c
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows.filter(r => r.some(v => String(v).trim() !== ''))
}

// Rows -> array of objects keyed by trimmed header
export function rowsToObjects(rows) {
  if (!rows.length) return { header: [], records: [] }
  const header = rows[0].map(h => String(h).trim())
  const records = rows.slice(1).map(r => {
    const o = {}
    header.forEach((h, i) => { o[h] = r[i] !== undefined ? String(r[i]).trim() : '' })
    return o
  })
  return { header, records }
}

// Number cleaner: "1,234" -> 1234, "(12)" -> -12, "n/a"/"" -> null
export function toNumber(v) {
  if (v === null || v === undefined) return null
  let s = String(v).trim()
  if (!s || /^n\/?a$/i.test(s) || s === '-' || s === '—') return null
  let neg = false
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1) }
  s = s.replace(/[$,\s]/g, '')
  const n = Number(s)
  if (Number.isNaN(n)) return null
  return neg ? -n : n
}

// SKU normalisation. Keeps SKUs as text, strips stray leading "*" and whitespace, upper-cases for matching.
export function normSku(s) {
  return String(s || '').trim().replace(/^\*+/, '').trim().toUpperCase()
}

// Spreadsheets turn SKUs like "01-05-014" into dates ("2014-01-05" or "1/5/2014").
// Returns the likely original SKU, or null if the value doesn't look date-mangled.
export function unmangleDateSku(s) {
  const v = String(s || '').trim()
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T].*)?$/)
  if (m) return `${m[2]}-${m[3]}-${m[1].slice(1)}`
  m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (m) return `${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}-${m[3].slice(1)}`
  return null
}

// Build a SKU lookup that tolerates the problems above.
export function makeSkuMatcher(products) {
  const bySku = {}
  products.forEach(p => { if (p.sku) bySku[normSku(p.sku)] = p })
  return raw => {
    const direct = bySku[normSku(raw)]
    if (direct) return { product: direct, fixedFrom: null }
    const un = unmangleDateSku(raw)
    if (un && bySku[normSku(un)]) return { product: bySku[normSku(un)], fixedFrom: String(raw).trim() }
    return { product: null, fixedFrom: null }
  }
}

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 }

// Parse a month header into "YYYY-MM-01". Accepts 2026-09-01, 9/1/2026, 9/2026, Sep-26, Sep 2026, September 2026,
// Excel serials (46266) and "Fcst Oct-26". Returns null when the header isn't a month.
export function parseMonthHeader(h) {
  let s = String(h || '').trim().replace(/^fcst\s+/i, '')
  if (!s) return null
  const pad = n => String(n).padStart(2, '0')
  let m = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/)
  if (m) return `${m[1]}-${pad(m[2])}-01`
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (m) { const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]); return `${y}-${pad(m[1])}-01` }
  m = s.match(/^(\d{1,2})\/(\d{4})$/)
  if (m) return `${m[2]}-${pad(m[1])}-01`
  m = s.match(/^([A-Za-z]+)[\s\-'’]*(\d{2}|\d{4})$/)
  if (m && MONTHS[m[1].toLowerCase().slice(0, 3)] !== undefined) {
    const mon = MONTHS[m[1].toLowerCase().slice(0, 3)]
    const y = m[2].length === 2 ? 2000 + Number(m[2]) : Number(m[2])
    return `${y}-${pad(mon + 1)}-01`
  }
  if (/^\d{5}$/.test(s)) { // Excel serial date
    const d = new Date(Date.UTC(1899, 11, 30) + Number(s) * 86400000)
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-01`
  }
  return null
}

// ── Sales forecast (layout of the "Sales Forecast" tab) ──────────────────────
// Columns: Channel | SKU | Description | ASIN | (blank) | month | month | ...
// Only rows whose Channel is Amazon / Direct / Other are read; the Total section is ignored.
export function parseForecast(text, products) {
  const rows = parseCSVRows(text)
  if (!rows.length) return { error: 'The file is empty.' }
  // Find the header row: the first row that has a "Channel" and "SKU" cell
  const hIdx = rows.findIndex(r => r.some(c => /^channel$/i.test(String(c).trim())) && r.some(c => /^sku$/i.test(String(c).trim())))
  if (hIdx === -1) return { error: 'Could not find a header row with "Channel" and "SKU" columns.' }
  const header = rows[hIdx].map(c => String(c).trim())
  const chCol = header.findIndex(c => /^channel$/i.test(c))
  const skuCol = header.findIndex(c => /^sku$/i.test(c))
  const descCol = header.findIndex(c => /^description$/i.test(c))
  const monthCols = header.map((c, i) => ({ i, month: parseMonthHeader(c) })).filter(x => x.month)
  if (!monthCols.length) return { error: 'No month columns found. Month headers should look like 2026-10-01, 10/1/2026 or Oct-26.' }

  const match = makeSkuMatcher(products)
  const lines = []
  const unmatched = {}
  const fixedSkus = {}
  for (const r of rows.slice(hIdx + 1)) {
    const chRaw = String(r[chCol] || '').trim().toLowerCase()
    const channel = chRaw === 'amazon' ? 'amazon' : (chRaw === 'direct' || chRaw === 'shopify' || chRaw === 'dtc') ? 'direct' : chRaw === 'other' ? 'other' : null
    const rawSku = String(r[skuCol] || '').trim()
    if (!channel || !rawSku) continue
    const { product, fixedFrom } = match(rawSku)
    if (fixedFrom) fixedSkus[fixedFrom] = product.sku
    const sku = product ? product.sku : rawSku
    for (const { i, month } of monthCols) {
      const units = toNumber(r[i])
      if (units === null) continue
      lines.push({ sku, product_id: product?.id || null, channel, month, units })
    }
    if (!product) {
      const total = monthCols.reduce((a, { i }) => a + (toNumber(r[i]) || 0), 0)
      if (total !== 0) {
        unmatched[rawSku] = unmatched[rawSku] || { sku: rawSku, description: descCol >= 0 ? r[descCol] : '', units: 0 }
        unmatched[rawSku].units += total
      }
    }
  }
  // Collapse duplicates (same sku/channel/month) by summing
  const key = l => `${l.sku}|${l.channel}|${l.month}`
  const merged = {}
  lines.forEach(l => { merged[key(l)] = merged[key(l)] ? { ...merged[key(l)], units: merged[key(l)].units + l.units } : { ...l } })
  return {
    lines: Object.values(merged),
    months: [...new Set(monthCols.map(m => m.month))].sort(),
    unmatched: Object.values(unmatched),
    fixedSkus
  }
}

// ── Shopify inventory export ────────────────────────────────────────────────
// Shopify "Export inventory" CSV: Handle, Title, Option1 Name ... SKU, HS Code, COO, <one column per location>
const SHOPIFY_FIXED_COLS = new Set(['handle', 'title', 'option1 name', 'option1 value', 'option2 name', 'option2 value',
  'option3 name', 'option3 value', 'sku', 'hs code', 'coo', 'barcode', 'variant sku', 'location', 'incoming', 'unavailable',
  'committed', 'available', 'on hand'])

export function readShopifyInventoryHeader(text) {
  const rows = parseCSVRows(text)
  if (!rows.length) return { error: 'The file is empty.' }
  const { header, records } = rowsToObjects(rows)
  const skuCol = header.find(h => /^(variant )?sku$/i.test(h))
  if (!skuCol) return { error: 'No "SKU" column found — is this the Shopify inventory export?' }
  // Location columns = everything that isn't a known product/variant attribute.
  // Newer exports add "Variant Packed Length/Width/Height/Dimension Unit" etc. — never treat those as locations.
  const notLocation = h => SHOPIFY_FIXED_COLS.has(h.toLowerCase()) || /^variant\b/i.test(h) || /\b(length|width|height|weight|dimension|unit|price|cost|barcode|vendor|type|tags|status)\b/i.test(h)
  const locationCols = header.filter(h => h && !notLocation(h))
  return { header, records, skuCol, locationCols }
}

// Sum the selected location columns per SKU. Virtual bundles are skipped (their stock is really their components').
export function buildShopifyInventory({ records, skuCol, selectedCols, products }) {
  const match = makeSkuMatcher(products)
  const totals = {}
  const unknown = {}
  const skippedBundles = {}
  const fixedSkus = {}
  for (const r of records) {
    const raw = r[skuCol]
    if (!raw) continue
    const qty = selectedCols.reduce((a, c) => a + (toNumber(r[c]) || 0), 0) // "not stocked" -> 0
    const { product, fixedFrom } = match(raw)
    if (!product) {
      if (qty !== 0) unknown[raw] = (unknown[raw] || 0) + qty
      continue
    }
    if (fixedFrom) fixedSkus[fixedFrom] = product.sku
    if (product.product_type === 'virtual_bundle') { skippedBundles[product.sku] = (skippedBundles[product.sku] || 0) + qty; continue }
    totals[product.id] = (totals[product.id] || 0) + qty
  }
  return {
    totals,
    unknown: Object.entries(unknown).map(([sku, qty]) => ({ sku, qty })),
    skippedBundles: Object.entries(skippedBundles).map(([sku, qty]) => ({ sku, qty })),
    fixedSkus
  }
}

// ── Amazon FBA inventory report ─────────────────────────────────────────────
// On hand = available + fc-transfer + Reserved FC Processing, summed per ASIN (several seller SKUs can share an ASIN).
// Inbound is reported separately for reconciliation; it is NOT counted as on hand.
export function buildAmazonInventory({ text, products }) {
  const rows = parseCSVRows(text)
  if (!rows.length) return { error: 'The file is empty.' }
  const { header, records } = rowsToObjects(rows)
  const col = name => header.find(h => h.toLowerCase() === name.toLowerCase())
  const asinCol = col('asin')
  const availCol = col('available') || col('afn-fulfillable-quantity')
  if (!asinCol || !availCol) return { error: 'Missing "asin" or "available" column — is this the FBA Inventory report?' }
  const fcTransferCol = col('fc-transfer') || col('afn-reserved-fc-transfers')
  const fcProcCol = col('Reserved FC Processing') || col('afn-reserved-fc-processing')
  // Inbound: Amazon splits inbound-quantity into working (shipment created, not sent), shipped (in transit) and
  // received (at the FC, still being checked in). Working is ignored — the app's planned transfer covers it.
  const inboundCol = col('inbound-quantity')
  const inShippedCol = col('inbound-shipped')
  const inReceivedCol = col('inbound-received')
  const hasSplit = !!(inShippedCol || inReceivedCol)
  const snapCol = col('snapshot-date')

  const byAsin = {}
  products.forEach(p => { if (p.asin) byAsin[p.asin.trim().toUpperCase()] = p })
  const agg = {}
  const unknown = {}
  let snapshotDate = null
  for (const r of records) {
    const asin = String(r[asinCol] || '').trim().toUpperCase()
    if (!asin) continue
    if (!snapshotDate && snapCol && r[snapCol]) snapshotDate = String(r[snapCol]).slice(0, 10)
    const available = toNumber(r[availCol]) || 0
    const fcTransfer = fcTransferCol ? (toNumber(r[fcTransferCol]) || 0) : 0
    const fcProcessing = fcProcCol ? (toNumber(r[fcProcCol]) || 0) : 0
    const inShipped = hasSplit ? (toNumber(r[inShippedCol]) || 0) : (inboundCol ? (toNumber(r[inboundCol]) || 0) : 0)
    const inReceived = hasSplit ? (toNumber(r[inReceivedCol]) || 0) : 0
    const inbound = inShipped + inReceived
    const product = byAsin[asin]
    if (!product) {
      const q = available + fcTransfer + fcProcessing
      if (q || inbound) unknown[asin] = { asin, sku: r[col('sku')] || '', qty: (unknown[asin]?.qty || 0) + q, inbound: (unknown[asin]?.inbound || 0) + inbound }
      continue
    }
    const a = agg[product.id] || { product, available: 0, fcTransfer: 0, fcProcessing: 0, inbound: 0, inShipped: 0, inReceived: 0, sellerSkus: [] }
    a.available += available; a.fcTransfer += fcTransfer; a.fcProcessing += fcProcessing; a.inbound += inbound; a.inShipped += inShipped; a.inReceived += inReceived
    if (r[col('sku')]) a.sellerSkus.push(r[col('sku')])
    agg[product.id] = a
  }
  return { byProduct: agg, unknown: Object.values(unknown), snapshotDate, hasFcProcessing: !!fcProcCol }
}
